import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import type { QueryClient } from '@tanstack/react-query';
import { Suspense, lazy, type ReactNode } from 'react';

import DashboardView from '../containers/DashboardView';
import { RARITY_VALUES } from '../constants';
import { playerDetailQueryOptions } from '../queries/playerQueries';
import AppLayout from './AppLayout';

const AuctionsView = lazy(() => import('../containers/AuctionsView'));
const MarketView = lazy(() => import('../containers/MarketView'));
const InstantBuyView = lazy(() => import('../containers/InstantBuyView'));
const AllCardsView = lazy(() => import('../containers/AllCardsView'));
const SettingsView = lazy(() => import('../containers/SettingsView'));
const TopPlayersView = lazy(() => import('../containers/TopPlayersView'));
const TopNbaPlayersView = lazy(() => import('../containers/TopNbaPlayersView'));
const PlayerView = lazy(() => import('../containers/PlayerView'));
const NbaPlayerView = lazy(() => import('../containers/NbaPlayerView'));
const ManagerCardsView = lazy(() => import('../containers/ManagerCardsView'));
const NbaPlayersLineupView = lazy(() => import('../containers/NbaPlayersLineupView'));

const LoadingFallback = () => (
  <div style={{ padding: 32, textAlign: 'center' }}>
    <div>Loading...</div>
  </div>
);

const withSuspense = (element: ReactNode) => (
  <Suspense fallback={<LoadingFallback />}>{element}</Suspense>
);

export const createAppRouter = (queryClient: QueryClient) => {
  const routes: RouteObject[] = [
    {
      path: '/',
      element: <AppLayout />,
      children: [
        { index: true, element: <DashboardView /> },
        { path: 'auctions', element: withSuspense(<AuctionsView />) },
        { path: 'market', element: withSuspense(<MarketView />) },
        { path: 'instant-buy', element: withSuspense(<InstantBuyView />) },
        { path: RARITY_VALUES.ALL, element: withSuspense(<AllCardsView />) },
        { path: 'settings', element: withSuspense(<SettingsView />) },
        { path: 'top', element: withSuspense(<TopPlayersView />) },
        { path: 'nba/top', element: withSuspense(<TopNbaPlayersView />) },
        {
          path: 'player/:slug',
          loader: async ({ params }) => {
            const slug = params.slug;
            if (slug) {
              // Prefetch best-effort only. If the request fails (e.g. the API
              // now returns 500 on a DB error, or 404 for an unknown slug), let
              // the route still render so PlayerView's error boundary shows the
              // styled QueryErrorState instead of React Router's bare default
              // error page. (PlayerView reads the player via useSuspenseQuery,
              // which re-runs the query and throws to that boundary.)
              try {
                await queryClient.ensureQueryData(playerDetailQueryOptions(slug));
              } catch {
                // Swallowed: PlayerView re-runs the query; its error boundary renders the error UI.
              }
            }
            return null;
          },
          element: withSuspense(<PlayerView />),
        },
        { path: 'nba/player/:slug', element: withSuspense(<NbaPlayerView />) },
        { path: 'manager/:slug/cards', element: withSuspense(<ManagerCardsView />) },
        { path: 'manager/:slug/nba/lineup', element: withSuspense(<NbaPlayersLineupView />) },
      ],
    },
  ];

  return createBrowserRouter(routes);
};

export type AppRouter = ReturnType<typeof createAppRouter>;
